import { useMemo } from 'react';
import { useCustom } from '@refinedev/core';
import type { Field } from 'react-querybuilder';
import type { RQBFieldOut } from '@/api/generated/schemas';
import { mapField } from './fieldMappings';

/**
 * Loads the available publication filter fields from
 * `GET /user_catalog/publications/filter_fields` and maps them to
 * react-querybuilder `Field[]`. The field set is static, so it is cached
 * indefinitely for the session.
 *
 * Note: a RELATIVE url is used (resolved against the axios baseURL). An absolute
 * url would be mangled by `dataProvider.custom`, which replaces all dots with
 * slashes (intended for dot-notation resources) — that breaks dotted hostnames.
 */
export const useFilterFields = (): { fields: Field[]; isLoading: boolean } => {
  const { query } = useCustom<RQBFieldOut[]>({
    url: `user_catalog/publications/filter_fields`,
    method: 'get',
    queryOptions: {
      queryKey: ['publication-filter-fields'],
      staleTime: Infinity,
    },
  });

  const raw = query.data?.data;

  const fields = useMemo(
    () => (Array.isArray(raw) ? raw.map(mapField) : []),
    [raw]
  );

  return { fields, isLoading: query.isLoading };
};
